import {
  LYRICS_WIDTH,
  MIN_AVATAR_LYRICS_GAP,
  MIN_LYRICS_HEIGHT,
  MIN_LYRICS_PRODUCE_GAP,
  MIN_PRODUCE_CARD_GAP,
  PRODUCE_HEIGHT,
  PRODUCE_WIDTH,
  STACKED_BREAKPOINT,
  clamp,
} from './constants';
import type { NodeLayout, NodeSize, WorkflowMode } from './types';

const DEFAULT_PRODUCE_SIZE: NodeSize = {
  width: PRODUCE_WIDTH,
  height: PRODUCE_HEIGHT,
};

interface ComputeLayoutArgs {
  mode: WorkflowMode;
  viewport: { width: number; height: number };
  produceSize?: NodeSize;
  isStationMode?: boolean;
}

export const computeDefaultPositions = ({
  mode,
  viewport,
  produceSize = DEFAULT_PRODUCE_SIZE,
  isStationMode = false,
}: ComputeLayoutArgs): Record<string, NodeLayout> => {
  const layouts: Record<string, NodeLayout> = {};
  const { width, height } = viewport;
  const isMusic = mode === 'music';
  const cardAspectWidth = mode === 'image' ? 16 : 39;
  const cardAspectHeight = mode === 'image' ? 9 : 50;
  const cardAspectRatio = cardAspectWidth / cardAspectHeight;
  const minCardHeight = isMusic ? 240 : clamp(height * 0.18, 150, 240);
  const maxCardHeight = isMusic ? 460 : clamp(height * 0.3, 260, 360);
  const minCardWidth = isMusic ? 200 : clamp(width * 0.18, 240, 320);

  const stackedView =
    width < STACKED_BREAKPOINT || width / Math.max(height, 1) < 0.75;

  const topMargin = clamp(height * 0.08, 40, 140);
  const verticalSpacing = clamp(height * 0.05, 28, 84);
  const bottomMargin = clamp(height * 0.12, 72, 200);

  if (stackedView) {
    const centerX = width / 2;
    const avatarDiameter = 160; // Fixed size matching #avatar-circle (w-40 h-40)
    const avatarRadius = avatarDiameter / 2;
    const avatarY = topMargin;

    layouts.avatar = {
      position: { x: centerX - avatarRadius, y: avatarY },
      size: { width: avatarDiameter, height: avatarDiameter },
    };

    let cursorY = isStationMode
      ? topMargin
      : avatarY + avatarDiameter + verticalSpacing;

    if (!isMusic) {
      const extraGap = clamp(
        height * 0.04,
        verticalSpacing * 0.4,
        verticalSpacing * 0.9,
      );
      cursorY += extraGap;
    }

    if (isMusic) {
      const lyricsWidth = clamp(width - 48, 260, width - 32);
      layouts.lyrics = {
        position: { x: centerX - lyricsWidth / 2, y: cursorY },
        size: { width: lyricsWidth, height: MIN_LYRICS_HEIGHT },
      };
      cursorY += MIN_LYRICS_HEIGHT + verticalSpacing;

      const produceWidth = clamp(
        produceSize.width,
        PRODUCE_WIDTH * 0.9,
        clamp(width - 96, produceSize.width, width - 48),
      );
      const produceHeight = clamp(
        produceSize.height,
        36,
        clamp(height * 0.12, PRODUCE_HEIGHT, 90),
      );
      layouts.produce = {
        position: { x: centerX - produceWidth / 2, y: cursorY },
        size: { width: produceWidth, height: produceHeight },
      };
      cursorY += produceHeight + verticalSpacing;
      const produceToCardsGap = clamp(
        height * 0.05,
        verticalSpacing,
        verticalSpacing * 1.4,
      );
      cursorY += produceToCardsGap;
    } else {
      delete layouts.lyrics;
      delete layouts.produce;
    }

    const horizontalGap = clamp(width * 0.06, 24, 64);
    const sidePadding = clamp(width * 0.08, 24, 80);
    const usableWidth = Math.max(width - sidePadding * 2, 200);
    const perCardMaxWidth = (usableWidth - horizontalGap) / 2;
    const remainingHeight = Math.max(height - cursorY - bottomMargin, 320);
    const maxCardWidth = Math.max(perCardMaxWidth, 1);
    const effectiveMinCardWidth = Math.min(minCardWidth, maxCardWidth);
    const maxHeightByWidth = maxCardWidth / cardAspectRatio;
    const preferredHeight = Math.min(remainingHeight, maxHeightByWidth);
    const effectiveMaxHeight = Math.min(maxCardHeight, remainingHeight);

    const computeCardSize = (targetHeight: number) => {
      let heightValue = clamp(targetHeight, minCardHeight, effectiveMaxHeight);
      let widthValue = heightValue * cardAspectRatio;

      if (widthValue > maxCardWidth) {
        widthValue = maxCardWidth;
        heightValue = clamp(
          widthValue / cardAspectRatio,
          minCardHeight,
          effectiveMaxHeight,
        );
      } else if (widthValue < effectiveMinCardWidth) {
        widthValue = effectiveMinCardWidth;
        heightValue = clamp(
          widthValue / cardAspectRatio,
          minCardHeight,
          effectiveMaxHeight,
        );
      }

      // Ensure final width respects bounds after height adjustments
      widthValue = clamp(
        heightValue * cardAspectRatio,
        effectiveMinCardWidth,
        maxCardWidth,
      );
      heightValue = clamp(
        widthValue / cardAspectRatio,
        minCardHeight,
        effectiveMaxHeight,
      );

      return { width: widthValue, height: heightValue };
    };

    const { width: cardWidth, height: cardHeight } =
      computeCardSize(preferredHeight);
    const rowY = cursorY;
    const totalRowWidth = cardWidth * 2 + horizontalGap;
    const startX = centerX - totalRowWidth / 2;

    layouts.card1 = {
      position: { x: startX, y: rowY },
      size: { width: cardWidth, height: cardHeight },
    };
    layouts.card2 = {
      position: { x: startX + cardWidth + horizontalGap, y: rowY },
      size: { width: cardWidth, height: cardHeight },
    };

    return layouts;
  }

  const avatarDiameter = 160; // Fixed size matching #avatar-circle (w-40 h-40)
  const avatarRadius = avatarDiameter / 2;
  const avatarX = clamp(width * 0.08, avatarRadius + 40, width * 0.16);
  const avatarY = clamp(height * 0.45, avatarRadius + 40, height * 0.45);
  const avatarCenterY = avatarY + avatarDiameter / 2;

  layouts.avatar = {
    position: { x: avatarX, y: avatarY },
    size: { width: avatarDiameter, height: avatarDiameter },
  };

  const rightColumnX = clamp(width * 0.68, width * 0.5, width * 0.75);
  const topCardY = clamp(height * 0.18, 60, height * 0.3);
  const minVerticalGap = clamp(height * 0.06, 48, 140);
  const verticalMargins =
    clamp(height * 0.22, 160, 320) + clamp(height * 0.18, 120, 260);
  const availableHeightForCards = Math.max(
    height - verticalMargins,
    minVerticalGap * 2 + 520,
  );
  const desiredCardHeight = clamp(
    (availableHeightForCards - minVerticalGap) / 2,
    minCardHeight,
    maxCardHeight,
  );
  const rawDesktopMaxWidth = clamp(
    width * (isMusic ? 0.26 : 0.32),
    minCardWidth,
    isMusic ? 360 : 460,
  );
  const maxCardWidth = Math.max(rawDesktopMaxWidth, minCardWidth);
  const effectiveMinCardWidth = Math.min(minCardWidth, maxCardWidth);

  const computeDesktopCardSize = (targetHeight: number) => {
    const heightBounds = {
      min: minCardHeight,
      max: Math.min(maxCardHeight, availableHeightForCards),
    };
    let heightValue = clamp(targetHeight, heightBounds.min, heightBounds.max);
    let widthValue = heightValue * cardAspectRatio;

    if (widthValue > maxCardWidth) {
      widthValue = maxCardWidth;
      heightValue = clamp(
        widthValue / cardAspectRatio,
        heightBounds.min,
        heightBounds.max,
      );
    } else if (widthValue < effectiveMinCardWidth) {
      widthValue = effectiveMinCardWidth;
      heightValue = clamp(
        widthValue / cardAspectRatio,
        heightBounds.min,
        heightBounds.max,
      );
    }

    widthValue = clamp(
      heightValue * cardAspectRatio,
      effectiveMinCardWidth,
      maxCardWidth,
    );
    heightValue = clamp(
      widthValue / cardAspectRatio,
      heightBounds.min,
      heightBounds.max,
    );

    return { width: widthValue, height: heightValue };
  };

  const { width: cardWidth, height: cardHeight } =
    computeDesktopCardSize(desiredCardHeight);
  const availableGapSpace = Math.max(
    height - (topCardY + cardHeight * 2) - clamp(height * 0.1, 56, 180),
    minVerticalGap,
  );
  const cardVerticalSpacing = availableGapSpace;

  layouts.card1 = {
    position: { x: rightColumnX, y: topCardY },
    size: { width: cardWidth, height: cardHeight },
  };
  layouts.card2 = {
    position: {
      x: rightColumnX,
      y: clamp(
        topCardY + cardHeight + cardVerticalSpacing,
        topCardY + cardHeight + 60,
        height - cardHeight - clamp(height * 0.1, 48, 140),
      ),
    },
    size: { width: cardWidth, height: cardHeight },
  };

  if (isMusic) {
    const lyricsWidth = Math.min(LYRICS_WIDTH, width * 0.3);
    const avatarGapBase = clamp(width * 0.075, 60, 190);
    const desiredLyricsX = avatarX + avatarDiameter + avatarGapBase;
    const minLyricsX = avatarX + avatarDiameter + MIN_AVATAR_LYRICS_GAP;
    const maxLyricsX = rightColumnX - lyricsWidth - clamp(width * 0.1, 50, 190);
    const lyricsX = clamp(
      desiredLyricsX,
      minLyricsX,
      Math.max(minLyricsX, maxLyricsX),
    );

    const lyricsY = clamp(
      avatarCenterY - MIN_LYRICS_HEIGHT / 2,
      clamp(height * 0.06, 32, 160),
      height - MIN_LYRICS_HEIGHT - clamp(height * 0.1, 60, 220),
    );

    layouts.lyrics = {
      position: { x: lyricsX, y: lyricsY },
      size: { width: lyricsWidth, height: MIN_LYRICS_HEIGHT },
    };

    const produceWidth = clamp(
      produceSize.width,
      PRODUCE_WIDTH * 0.9,
      Math.min(PRODUCE_WIDTH * 1.4, width * 0.28),
    );
    const produceHeight = clamp(
      produceSize.height,
      36,
      clamp(height * 0.12, PRODUCE_HEIGHT, 90),
    );
    const produceY = clamp(
      avatarCenterY - produceHeight / 2,
      clamp(height * 0.08, 32, 160),
      height - produceHeight - clamp(height * 0.1, 48, 220),
    );
    const horizontalGapBase = clamp(width * 0.05, 36, 96);
    const produceGapLeft = Math.max(horizontalGapBase, MIN_LYRICS_PRODUCE_GAP);
    const producePreferredX = lyricsX + lyricsWidth + produceGapLeft;
    const minProduceX = avatarX + avatarDiameter + produceGapLeft;
    const maxProduceXRaw = width - produceWidth - MIN_PRODUCE_CARD_GAP;
    const maxProduceX = Math.max(minProduceX, maxProduceXRaw);
    const produceX = stackedView
      ? width / 2 - produceWidth / 2
      : clamp(producePreferredX, minProduceX, maxProduceX);

    layouts.produce = {
      position: { x: produceX, y: produceY },
      size: { width: produceWidth, height: produceHeight },
    };

    if (!stackedView) {
      const produceRightEdge = produceX + produceWidth;
      const cardMinX = Math.max(
        rightColumnX,
        produceRightEdge + MIN_PRODUCE_CARD_GAP,
      );
      const cardMaxX = Math.max(
        cardMinX,
        width -
          (layouts.card1?.size?.width ?? 0) -
          clamp(width * 0.05, 24, 160),
      );

      if (layouts.card1) {
        layouts.card1 = {
          ...layouts.card1,
          position: {
            ...layouts.card1.position,
            x: clamp(cardMinX, rightColumnX, cardMaxX),
          },
        };
      }
      if (layouts.card2) {
        layouts.card2 = {
          ...layouts.card2,
          position: {
            ...layouts.card2.position,
            x: clamp(cardMinX, rightColumnX, cardMaxX),
          },
        };
      }
    }
  } else {
    const helperX = avatarX + avatarDiameter + clamp(width * 0.04, 32, 90);
    const lyricsWidth = Math.min(LYRICS_WIDTH, width * 0.32);
    const lyricsY = clamp(
      avatarCenterY - MIN_LYRICS_HEIGHT / 2,
      clamp(height * 0.06, 32, 160),
      height - MIN_LYRICS_HEIGHT - clamp(height * 0.1, 60, 220),
    );
    layouts.lyrics = {
      position: { x: helperX, y: lyricsY },
      size: { width: lyricsWidth, height: MIN_LYRICS_HEIGHT },
    };

    const produceHeight = clamp(
      produceSize.height,
      36,
      clamp(height * 0.12, PRODUCE_HEIGHT, 90),
    );
    const produceY = clamp(
      avatarCenterY - produceHeight / 2,
      clamp(height * 0.08, 32, 160),
      height - produceHeight - clamp(height * 0.1, 48, 220),
    );
    layouts.produce = {
      position: { x: helperX, y: produceY },
      size: { width: PRODUCE_WIDTH, height: produceHeight },
    };
  }

  return layouts;
};
